// src/features/dashboard/pages/TeamActiveHours.tsx

import {
  useEffect,
  useRef,
  useState,
} from 'react';

import ActivityDetailsModal from '../components/ActivityDetailsModal';
import DashboardHeader from '../components/DashboardHeader';
import TeamKpiCards from '../components/TeamKpiCards';
import TeamLeaderDropdown from '../components/TeamLeaderDropdown';
import LocationDropdown from '../components/LocationDropdown';
import TeamActiveHoursTable from '../components/TeamActiveHoursTable';

import {
  ManagerDateProvider,
  useManagerDate,
} from '../context/ManagerDateContext';

import {
  getManagerEmployeeCode,
} from '../../../features/auth/utils/Login/authStorage';

/* ============================================================================
 * BROWSER ZOOM AUTO REFRESH
 * ========================================================================== */

/**
 * Automatically reloads this page when browser/page zoom changes.
 *
 * Detection is intentionally layered because Chrome does not expose a
 * universal "page zoom changed" event:
 *
 * 1. Ctrl/Cmd + + / - / 0 keyboard zoom shortcuts
 * 2. Ctrl/Cmd + mouse-wheel zoom
 * 3. devicePixelRatio changes
 * 4. Chrome resize signal where innerWidth changes but outerWidth remains
 *    effectively unchanged (typical page-zoom behaviour)
 * 5. CSS resolution media-query changes
 * 6. polling fallback
 *
 * The reload is debounced and guarded so one zoom action causes only one
 * page reload.
 */
const useBrowserZoomAutoRefresh = () => {
  const initialDprRef = useRef(
    Number(
      window.devicePixelRatio.toFixed(3),
    ),
  );

  const initialInnerWidthRef = useRef(
    window.innerWidth,
  );

  const initialInnerHeightRef = useRef(
    window.innerHeight,
  );

  const initialOuterWidthRef = useRef(
    window.outerWidth,
  );

  const initialOuterHeightRef = useRef(
    window.outerHeight,
  );

  const reloadStartedRef =
    useRef(false);

  const reloadTimerRef =
    useRef<number | null>(null);

  const resizeTimerRef =
    useRef<number | null>(null);

  const pollingTimerRef =
    useRef<number | null>(null);

  const resolutionQueryRef =
    useRef<MediaQueryList | null>(null);

  const resolutionHandlerRef =
    useRef<(() => void) | null>(null);

  useEffect(() => {
    /*
     * Capture the current browser state after the page has mounted.
     * These values become the baseline for detecting a later zoom change.
     */
    initialDprRef.current =
      Number(
        window.devicePixelRatio.toFixed(3),
      );

    initialInnerWidthRef.current =
      window.innerWidth;

    initialInnerHeightRef.current =
      window.innerHeight;

    initialOuterWidthRef.current =
      window.outerWidth;

    initialOuterHeightRef.current =
      window.outerHeight;

    reloadStartedRef.current = false;

    const reloadPage = (
      reason: string,
    ) => {
      if (reloadStartedRef.current) {
        return;
      }

      reloadStartedRef.current = true;

      console.log(
        '[TeamActiveHours] Browser zoom changed. Reloading page.',
        reason,
      );

      if (
        reloadTimerRef.current !== null
      ) {
        window.clearTimeout(
          reloadTimerRef.current,
        );
      }

      /*
       * Small delay is important.
       * Chrome needs a moment to finish applying the new zoom level before
       * the reload occurs.
       */
      reloadTimerRef.current =
        window.setTimeout(() => {
          window.location.reload();
        }, 300);
    };

    /*
     * ---------------------------------------------------------------
     * DIRECT ZOOM KEYBOARD DETECTION
     * ---------------------------------------------------------------
     *
     * This is the most reliable detector when the user uses:
     *
     * Ctrl + +
     * Ctrl + -
     * Ctrl + 0
     *
     * or Cmd on macOS.
     */
    const handleKeyDown = (
      event: KeyboardEvent,
    ) => {
      const modifierPressed =
        event.ctrlKey ||
        event.metaKey;

      if (!modifierPressed) {
        return;
      }

      const zoomKey =
        event.key === '+' ||
        event.key === '=' ||
        event.key === '-' ||
        event.key === '_' ||
        event.key === '0';

      if (!zoomKey) {
        return;
      }

      /*
       * Do not reload immediately because Chrome has not applied the new
       * zoom level yet. Wait for it to settle.
       */
      window.setTimeout(() => {
        reloadPage(
          `keyboard zoom shortcut: ${event.key}`,
        );
      }, 250);
    };

    window.addEventListener(
      'keydown',
      handleKeyDown,
    );

    /*
     * ---------------------------------------------------------------
     * CTRL/CMD + MOUSE WHEEL ZOOM
     * ---------------------------------------------------------------
     */
    const handleWheel = (
      event: WheelEvent,
    ) => {
      if (
        event.ctrlKey ||
        event.metaKey
      ) {
        window.setTimeout(() => {
          reloadPage(
            'Ctrl/Cmd + mouse-wheel zoom',
          );
        }, 250);
      }
    };

    window.addEventListener(
      'wheel',
      handleWheel,
      {
        passive: true,
      },
    );

    /*
     * ---------------------------------------------------------------
     * GENERIC ZOOM CHECK
     * ---------------------------------------------------------------
     */
    const checkZoom = () => {
      if (reloadStartedRef.current) {
        return;
      }

      const currentDpr =
        Number(
          window.devicePixelRatio.toFixed(3),
        );

      const currentInnerWidth =
        window.innerWidth;

      const currentInnerHeight =
        window.innerHeight;

      const currentOuterWidth =
        window.outerWidth;

      const currentOuterHeight =
        window.outerHeight;

      /*
       * Signal 1:
       * devicePixelRatio changed.
       */
      const dprChanged =
        Math.abs(
          currentDpr -
            initialDprRef.current,
        ) >= 0.01;

      if (dprChanged) {
        reloadPage(
          'devicePixelRatio changed',
        );
        return;
      }

      /*
       * Signal 2:
       * Typical Chrome page zoom behaviour.
       *
       * Page zoom changes the CSS viewport (innerWidth/innerHeight),
       * while the actual browser window dimensions (outerWidth/outerHeight)
       * remain almost unchanged.
       *
       * A normal window resize changes outerWidth/outerHeight too.
       */
      const innerWidthChanged =
        currentInnerWidth !==
        initialInnerWidthRef.current;

      const innerHeightChanged =
        currentInnerHeight !==
        initialInnerHeightRef.current;

      const outerWidthStable =
        Math.abs(
          currentOuterWidth -
            initialOuterWidthRef.current,
        ) <= 2;

      const outerHeightStable =
        Math.abs(
          currentOuterHeight -
            initialOuterHeightRef.current,
        ) <= 2;

      if (
        (innerWidthChanged ||
          innerHeightChanged) &&
        outerWidthStable &&
        outerHeightStable
      ) {
        reloadPage(
          'viewport changed while browser window size stayed stable',
        );
        return;
      }
    };

    /*
     * ---------------------------------------------------------------
     * RESIZE EVENT
     * ---------------------------------------------------------------
     *
     * Chrome can fire several resize events while changing zoom.
     * Debounce them and inspect the final values.
     */
    const handleResize = () => {
      if (
        resizeTimerRef.current !== null
      ) {
        window.clearTimeout(
          resizeTimerRef.current,
        );
      }

      resizeTimerRef.current =
        window.setTimeout(() => {
          checkZoom();
        }, 200);
    };

    window.addEventListener(
      'resize',
      handleResize,
      {
        passive: true,
      },
    );

    /*
     * ---------------------------------------------------------------
     * VISUAL VIEWPORT
     * ---------------------------------------------------------------
     */
    const visualViewport =
      window.visualViewport;

    const handleVisualViewportResize =
      () => {
        window.setTimeout(() => {
          checkZoom();
        }, 100);
      };

    if (visualViewport) {
      visualViewport.addEventListener(
        'resize',
        handleVisualViewportResize,
      );
    }

    /*
     * ---------------------------------------------------------------
     * CSS RESOLUTION MEDIA QUERY
     * ---------------------------------------------------------------
     */
    const setupResolutionQuery = () => {
      const dpr =
        Number(
          window.devicePixelRatio.toFixed(3),
        );

      const query =
        window.matchMedia(
          `(resolution: ${dpr}dppx)`,
        );

      const handler = () => {
        window.setTimeout(() => {
          checkZoom();
        }, 100);
      };

      resolutionQueryRef.current =
        query;

      resolutionHandlerRef.current =
        handler;

      if (
        typeof query.addEventListener ===
        'function'
      ) {
        query.addEventListener(
          'change',
          handler,
        );
      } else {
        query.addListener(handler);
      }
    };

    setupResolutionQuery();

    /*
     * ---------------------------------------------------------------
     * POLLING FALLBACK
     * ---------------------------------------------------------------
     *
     * Some Chrome/Windows configurations do not reliably fire every
     * signal above. Polling gives us an additional fallback.
     */
    pollingTimerRef.current =
      window.setInterval(() => {
        checkZoom();
      }, 100);

    /*
     * Initial check.
     */
    checkZoom();

    /*
     * ---------------------------------------------------------------
     * CLEANUP
     * ---------------------------------------------------------------
     */
    return () => {
      window.removeEventListener(
        'keydown',
        handleKeyDown,
      );

      window.removeEventListener(
        'wheel',
        handleWheel,
      );

      window.removeEventListener(
        'resize',
        handleResize,
      );

      if (visualViewport) {
        visualViewport.removeEventListener(
          'resize',
          handleVisualViewportResize,
        );
      }

      if (
        resizeTimerRef.current !== null
      ) {
        window.clearTimeout(
          resizeTimerRef.current,
        );

        resizeTimerRef.current = null;
      }

      if (
        reloadTimerRef.current !== null
      ) {
        window.clearTimeout(
          reloadTimerRef.current,
        );

        reloadTimerRef.current = null;
      }

      if (
        pollingTimerRef.current !== null
      ) {
        window.clearInterval(
          pollingTimerRef.current,
        );

        pollingTimerRef.current = null;
      }

      const query =
        resolutionQueryRef.current;

      const handler =
        resolutionHandlerRef.current;

      if (query && handler) {
        if (
          typeof query.removeEventListener ===
          'function'
        ) {
          query.removeEventListener(
            'change',
            handler,
          );
        } else {
          query.removeListener(handler);
        }
      }

      resolutionQueryRef.current =
        null;

      resolutionHandlerRef.current =
        null;
    };
  }, []);
};


/* ============================================================================
 * CONTENT
 * ========================================================================== */

const TeamActiveHoursContent = () => {

  // ==========================================================================
  // BROWSER ZOOM AUTO REFRESH
  // ==========================================================================

  useBrowserZoomAutoRefresh();


  const {
    fromDate,
    toDate,
  } = useManagerDate();

  const ManagerEmployeeCode =
    getManagerEmployeeCode();

  /* ==========================================================================
   * DATE CHANGE LOADER
   * ======================================================================== */

  const [
    dateLoading,
    setDateLoading,
  ] = useState(false);

  const isFirstDateRender =
    useRef(true);

  useEffect(() => {
    /*
     * Do NOT show loader on first render.
     */
    if (
      isFirstDateRender.current
    ) {
      isFirstDateRender.current =
        false;

      return;
    }

    /*
     * Show loader only when date changes.
     */
    setDateLoading(true);

    /*
     * Small delay so the loader is visible
     * while the child API requests refresh.
     */
    const timer =
      window.setTimeout(() => {
        setDateLoading(false);
      }, 800);

    return () => {
      window.clearTimeout(timer);
    };
  }, [
    fromDate,
    toDate,
  ]);

  /* ==========================================================================
   * POPUP STATE
   * ======================================================================== */

  const [
    openPopup,
    setOpenPopup,
  ] = useState(false);

  const [
    selectedEmployee,
    setSelectedEmployee,
  ] = useState('');

  const [
    selectedTime,
    setSelectedTime,
  ] = useState('');

  const [
    selectedPopupEmployeeCode,
    setSelectedPopupEmployeeCode,
  ] = useState('');

  const [
    selectedLoggedDate,
    setSelectedLoggedDate,
  ] = useState('');

  /* ==========================================================================
   * FILTER STATE
   * ======================================================================== */

  const [
    selectedLeader,
    setSelectedLeader,
  ] = useState('');

  const [
    searchTerm,
    setSearchTerm,
  ] = useState('');

  const [
    selectedLocation,
    setSelectedLocation,
  ] = useState('');

  /* ==========================================================================
   * LOCATION DATA
   * ======================================================================== */

  const [
    locations,
    setLocations,
  ] = useState<
    { geo: string }[]
  >([]);

  /* ==========================================================================
   * LEGENDS
   * ======================================================================== */

  const LEGENDS: [
    string,
    string,
  ][] = [
    ['#FDE68A', '<5h'],
    ['#FCD34D', '5-7h'],
    ['#A7F3D0', '7-8h'],
    ['#6EE7B7', '8-9h'],
    ['#10B981', '>9h'],
  ];

  /* ==========================================================================
   * PAGE
   * ======================================================================== */

  return (
    <div
      className="
        min-h-screen
        space-y-5
        bg-[#F5F7FB]
      "
    >
      {/* ======================================================================
          DASHBOARD HEADER
          This stays visible while loading.
      ====================================================================== */}

      <DashboardHeader
        showBackButton
        breadcrumbs={[
          {
            label: 'Work Vista',
            path: '/',
          },
          {
            label:
              'Reporting Manager Dashboard',
            path: '/rm-dashboard',
          },
          {
            label:
              'Team Active Hours',
          },
        ]}
      />

      {/* ======================================================================
          CONTENT AREA
          Loader is restricted to this area.
      ====================================================================== */}

      <div
        className="
          relative
          z-0
          overflow-visible
          rounded-[18px]
          border
          border-[#E5E7EB]
          bg-white
          shadow-sm
        "
      >
        {/* ====================================================================
            LOCAL DATE LOADER
            Only covers content below DashboardHeader.
        ==================================================================== */}

        {dateLoading && (
          <div
            className="
              absolute
              inset-0
              z-50
              flex
              items-center
              justify-center
              bg-white/70
              backdrop-blur-[1px]
            "
          >
            <div
              className="
                flex
                flex-col
                items-center
                justify-center
                rounded-[12px]
                bg-white
                px-6
                py-5
                shadow-md
              "
            >
              {/* Spinner */}

              <div
                className="
                  h-[30px]
                  w-[30px]
                  animate-spin
                  rounded-full
                  border-[3px]
                  border-[#E5E7EB]
                  border-t-[#2563EB]
                "
              />

              {/* Text */}

              <p
                className="
                  mt-3
                  text-[12px]
                  font-semibold
                  text-[#344054]
                "
              >
                Loading data...
              </p>

              <p
                className="
                  mt-1
                  text-[10px]
                  text-[#98A2B3]
                "
              >
                Updating selected date
              </p>
            </div>
          </div>
        )}

        {/* ====================================================================
            PAGE HEADER
        ==================================================================== */}

        <div className="p-4">
          <div
            className="
              mb-4
              flex
              items-center
              gap-3
            "
          >
            <h1
              className="
                text-[14px]
                font-bold
                text-[#002B5B]
              "
            >
              Team Active Hours
            </h1>

            <div
              className="
                rounded-full
                bg-[#EDF4FF]
                px-3
                py-[4px]
                text-[10px]
                font-semibold
                text-[#2563EB]
              "
            >
              30-Day View
            </div>
          </div>

          {/* ==================================================================
              KPI CARDS
          ================================================================== */}

          <TeamKpiCards
            ManagerEmployeeCode={
              ManagerEmployeeCode
            }
          />
{/* ======================================================================
    FILTER + LEGEND CONTAINER
    ====================================================================== */}

<div
  className="
    relative
    z-[100]
    mt-4
    w-full
    overflow-visible
    rounded-[14px]
    border
    border-[#E5E7EB]
    bg-white
    px-4
    py-3
  "
>
  <div
    className="
      flex
      w-full
      min-w-0
      items-start
      justify-between
      gap-4
      overflow-visible
    "
  >
    {/* ==================================================================
        LEFT SIDE
        FILTERS
    ================================================================== */}

    <div
      className="
        relative
        z-[110]
        flex
        min-w-0
        flex-1
        items-center
        gap-2
        overflow-visible
      "
    >
      {/* ================================================================
          TEAM LEADER
      ================================================================ */}

      <div
        className="
          relative
          z-40
          w-[175px]
          min-w-[175px]
          shrink-0

          [@media(max-width:1200px)]:w-[155px]
          [@media(max-width:1200px)]:min-w-[155px]

          [@media(max-width:1050px)]:w-[140px]
          [@media(max-width:1050px)]:min-w-[140px]
        "
      >
        <TeamLeaderDropdown
          managerEmployeeCode={ManagerEmployeeCode}
          value={selectedLeader}
          onChange={setSelectedLeader}
        />
      </div>

      {/* ================================================================
          SEARCH
      ================================================================ */}

      <div
        className="
          relative
          z-10
          w-[205px]
          min-w-[205px]
          shrink-0

          [@media(max-width:1200px)]:w-[180px]
          [@media(max-width:1200px)]:min-w-[180px]

          [@media(max-width:1050px)]:w-[160px]
          [@media(max-width:1050px)]:min-w-[160px]
        "
      >
        <input
          type="text"
          placeholder="Search team member..."
          value={searchTerm}
          onChange={(event) =>
            setSearchTerm(event.target.value)
          }
          className="
            h-[38px]
            w-full
            rounded-[10px]
            border
            border-[#D0D5DD]
            bg-white
            pl-9
            pr-3
            text-[11px]
            text-[#344054]
            outline-none
            placeholder:text-[#98A2B3]
            focus:border-[#84CAFF]
          "
        />

        <span
          className="
            pointer-events-none
            absolute
            left-3
            top-1/2
            -translate-y-1/2
            text-[11px]
            text-[#98A2B3]
          "
        >
          🔍
        </span>
      </div>

      {/* ================================================================
          LOCATION
      ================================================================ */}

      <div
        className="
          relative
          z-30
          w-[150px]
          min-w-[150px]
          shrink-0

          [@media(max-width:1200px)]:w-[135px]
          [@media(max-width:1200px)]:min-w-[135px]

          [@media(max-width:1050px)]:w-[125px]
          [@media(max-width:1050px)]:min-w-[125px]
        "
      >
        <LocationDropdown
          locations={locations}
          value={selectedLocation}
          onChange={setSelectedLocation}
        />
      </div>
    </div>

    {/* ==================================================================
        RIGHT SIDE
        HOURS + NOTE
    ================================================================== */}

    <div
      className="
        relative
        z-10
        min-w-0
        shrink
        basis-[430px]
        grow-0

        [@media(max-width:1200px)]:basis-[390px]
        [@media(max-width:1050px)]:basis-[340px]
        [@media(max-width:950px)]:basis-[300px]
      "
    >
      {/* ================================================================
          HOURS LEGEND
      ================================================================ */}

      <div
        className="
          flex
          w-full
          min-w-0
          items-center
          justify-start
          gap-x-[7px]
          overflow-hidden
          whitespace-nowrap
          text-[9px]

          [@media(max-width:1200px)]:gap-x-[5px]
          [@media(max-width:1050px)]:gap-x-[4px]
          [@media(max-width:1050px)]:text-[8px]
        "
      >
        {/* Hours */}

        <span
          className="
            shrink-0
            whitespace-nowrap
            text-[#667085]
          "
        >
          Hours:
        </span>

        {/* Dynamic legends */}

        {LEGENDS.map(
          ([color, label], index) => (
            <div
              key={index}
              className="
                flex
                shrink-0
                items-center
                gap-1
              "
            >
              <div
                className="
                  h-[9px]
                  w-[9px]
                  shrink-0
                  rounded-[2px]

                  [@media(max-width:1050px)]:h-[8px]
                  [@media(max-width:1050px)]:w-[8px]
                "
                style={{
                  backgroundColor: color,
                }}
              />

              <span
                className="
                  whitespace-nowrap
                  text-[#667085]
                "
              >
                {label}
              </span>
            </div>
          ),
        )}

        {/* Leave */}

        <div
          className="
            flex
            shrink-0
            items-center
            gap-1
          "
        >
          <div
            className="
              h-[9px]
              w-[9px]
              shrink-0
              rounded-[2px]
              bg-[#DDD6FE]

              [@media(max-width:1050px)]:h-[8px]
              [@media(max-width:1050px)]:w-[8px]
            "
          />

          <span
            className="
              whitespace-nowrap
              text-[#667085]
            "
          >
            Leave
          </span>
        </div>

        {/* Holiday */}

        <div
          className="
            flex
            shrink-0
            items-center
            gap-1
          "
        >
          <div
            className="
              h-[9px]
              w-[9px]
              shrink-0
              rounded-[2px]
              border
              border-[#CBD5E1]
              bg-[#F8FAFC]

              [@media(max-width:1050px)]:h-[8px]
              [@media(max-width:1050px)]:w-[8px]
            "
          />

          <span
            className="
              whitespace-nowrap
              text-[#667085]
            "
          >
            Holiday
          </span>
        </div>
      </div>

      {/* ================================================================
          NOTE
      ================================================================ */}

      <div
        className="
          mt-2
          w-full
          min-w-0
          text-left
          text-[8.5px]
          font-normal
          leading-[13px]
          text-[#616060]

          [@media(max-width:1200px)]:text-[8px]

          [@media(max-width:1050px)]:text-[7.5px]
          [@media(max-width:1050px)]:leading-[11px]
        "
      >
        <b>Note:</b> All averages have been calculated based on the
        available data. If a duration is not available for an employee,
        that employee&apos;s unavailable duration has been excluded from
        the respective average calculation.
      </div>
    </div>
  </div>
</div>

        </div>

        {/* ====================================================================
            TABLE
        ==================================================================== */}

        <TeamActiveHoursTable
          ManagerEmployeeCode={
            ManagerEmployeeCode
          }
          selectedLeader={
            selectedLeader
          }
          searchTerm={
            searchTerm
          }
          selectedLocation={
            selectedLocation
          }
          onLocationsLoaded={
            setLocations
          }
          onOpenPopup={(
            employeeName,
            selectedTime,
            employeeCode,
            loggedDate,
          ) => {
            setOpenPopup(
              true,
            );

            setSelectedEmployee(
              employeeName,
            );

            setSelectedTime(
              selectedTime,
            );

            setSelectedPopupEmployeeCode(
              employeeCode,
            );

            setSelectedLoggedDate(
              loggedDate,
            );
          }}
        />

        {/* ====================================================================
            ACTIVITY MODAL
        ==================================================================== */}

        <ActivityDetailsModal
          open={
            openPopup
          }
          onClose={() =>
            setOpenPopup(
              false,
            )
          }
          employeeName={
            selectedEmployee
          }
          selectedTime={
            selectedTime
          }
          employeeCode={
            selectedPopupEmployeeCode
          }
          loggedDate={
            selectedLoggedDate
          }
        />
      </div>
    </div>
  );
};

/* ============================================================================
 * PAGE
 * ========================================================================== */

const TeamActiveHours = () => {
  return (
    <ManagerDateProvider>
      <TeamActiveHoursContent />
    </ManagerDateProvider>
  );
};

export default TeamActiveHours;